import React from 'react'
import { createRoot } from 'react-dom/client'
import { ArrowRight, Scale, ShieldCheck, Siren, Wallet } from 'lucide-react'
import { SiteHeader } from '../components/SiteHeader'
import type { ReactHeaderData } from '../bounties/common'
import './gradient_landing.css'

const processSteps = [
  ['01', 'Post a bounty', 'Attach funds to your content—statements, claims, advertisements, etc.'],
  ['02', 'Share the badge', 'The Truth Bounty badge signals that you stand behind your claims.'],
  ['03', 'Invite scrutiny', 'For a fee, challengers can bring evidence that your content is false or misleading.'],
  ['04', 'Respond to challenges', 'Authors can concede or defend their content and proceed to arbitration.'],
  ['05', 'Resolve with arbitration', 'Independent arbitrators resolve the dispute.'],
  ['06', 'Settle up', 'If the challenge succeeds, the challenger gets the Truth Bounty. If the challenge fails, the author keeps the money.'],
] as const

type LandingItem = {
  id: number
  title: string
  description: string
  author: { username: string }
  created_at: string
  reward: string
}

type LandingData = {
  header?: ReactHeaderData
  total_bounties: number
  active_bounties_count: number
  challenged_bounties_count: number
  total_value: string | number
  recent_active_bounties?: LandingItem[]
  recent_challenged_bounties?: LandingItem[]
}

declare global {
  interface Window {
    __LANDING_DATA__?: LandingData
    __DEMO_MODE__?: boolean
  }
}

const DEFAULT_DATA: LandingData = {
  header: undefined,
  total_bounties: 0,
  active_bounties_count: 0,
  challenged_bounties_count: 0,
  total_value: 0,
  recent_active_bounties: [],
  recent_challenged_bounties: [],
}

const editorialImages = [
  '/static/img/dithered-image-upscaled-4k.png',
  '/static/img/scales1.png',
  '/static/img/dithered-image.png',
]

function formatCurrency(value: string | number) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return `$${value}`
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(numeric)
}

function formatDate(dateValue: string) {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

function LandingCard({
  item,
  label,
  image,
  tone,
}: {
  item: LandingItem
  label: string
  image: string
  tone: 'light' | 'dark'
}) {
  return (
    <article className={`tb-landing-card tb-landing-card--${tone}`}>
      <div className="tb-landing-card__media">
        <img src={image} alt="" />
      </div>
      <div className="tb-landing-card__body">
        <div className="tb-landing-card__eyebrow">{label}</div>
        <h3>{item.title}</h3>
        <p>{item.description}</p>
        <div className="tb-landing-card__meta">
          <span>{item.author.username}</span>
          <span>{formatDate(item.created_at)}</span>
          <span>{formatCurrency(item.reward)}</span>
        </div>
        <a className="tb-landing-inline-link" href={`/bounties/${item.id}/react/`}>
          View bounty <ArrowRight size={16} />
        </a>
      </div>
    </article>
  )
}

function GradientLanding() {
  const data = window.__LANDING_DATA__ || DEFAULT_DATA
  const demoMode = Boolean(window.__DEMO_MODE__)
  const activeItems = data.recent_active_bounties || []
  const challengedItems = data.recent_challenged_bounties || []
  const processColumns = [processSteps.slice(0, 3), processSteps.slice(3, 6)]

  return (
    <div className="tb-landing-shell">
      <div className="tb-landing-bg-orb tb-landing-bg-orb--left" />
      <div className="tb-landing-bg-orb tb-landing-bg-orb--right" />

      <SiteHeader
        homeHref={data.header?.home_href}
        navLinks={data.header?.primary_links}
        exploreLinks={data.header?.explore_links}
        searchAction={data.header?.search_action}
        searchQuery={data.header?.search_query}
        searchPlaceholder={data.header?.search_placeholder}
        userMenu={data.header?.user_menu}
        showLogin={demoMode ? false : data.header?.show_login}
        showRegister={demoMode ? false : data.header?.show_register}
      />

      <main>
        <section className="tb-landing-hero">
          <div className="tb-landing-container tb-landing-hero__grid">
            <div className="tb-landing-hero__copy">
              <div className="tb-landing-kicker">Truth infrastructure for the modern world</div>
              <h1>Truth Bounties</h1>
              <p>
                Truth Bounties gives businesses, authors, and communities a powerful tool to prove credibility,
                challenge misinformation, and resolve disputes.
              </p>

              <div className="tb-landing-hero__actions">
                {!demoMode && (
                  <a className="tb-landing-primary-button tb-landing-primary-button--large" href="/accounts/register-react/">
                    Post a bounty
                  </a>
                )}
                <a className="tb-landing-secondary-button" href="#live-docket">
                  Explore the docket
                </a>
              </div>

              <div className="tb-landing-signal-grid">
                <div>
                  <span>Active value</span>
                  <strong>{formatCurrency(data.total_value)}</strong>
                </div>
                <div>
                  <span>Open bounties</span>
                  <strong>{data.active_bounties_count}</strong>
                </div>
                <div>
                  <span>Challenges in play</span>
                  <strong>{data.challenged_bounties_count}</strong>
                </div>
              </div>
            </div>

            <div className="tb-landing-hero__feature">
              <article className="tb-landing-feature-card tb-landing-feature-card--primary">
                <div className="tb-landing-feature-card__image">
                  <img src={editorialImages[0]} alt="" />
                </div>
                <div className="tb-landing-feature-card__content">
                  <div className="tb-landing-card__eyebrow">Signal Trust</div>
                  <h2>Back your claims. Build your reputation.</h2>
                  <p>
                    Attach a Truth Bounty to your statements. Challengers bring evidence.
                    Independent arbitrators settle the record.
                  </p>
                </div>
              </article>

              <div className="tb-landing-mini-grid">
                <article className="tb-landing-mini-card">
                  <Wallet size={18} />
                  <strong>Real accountability</strong>
                  <p>Financial stakes promote trust—there’s a cost to being wrong.</p>
                </article>
                <article className="tb-landing-mini-card">
                  <Scale size={18} />
                  <strong>Transparent review</strong>
                  <p>Challenges, defenses, and arbitration decisions are public.</p>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="tb-landing-metrics">
          <div className="tb-landing-container tb-landing-metrics__grid">
            <article className="tb-landing-metric-card">
              <div className="tb-landing-metric-card__label">Bounties on platform</div>
              <div className="tb-landing-metric-card__value">{data.total_bounties}</div>
            </article>
            <article className="tb-landing-metric-card">
              <div className="tb-landing-metric-card__label">Ready for challenge</div>
              <div className="tb-landing-metric-card__value">{data.active_bounties_count}</div>
            </article>
            <article className="tb-landing-metric-card">
              <div className="tb-landing-metric-card__label">Value at stake</div>
              <div className="tb-landing-metric-card__value">{formatCurrency(data.total_value)}</div>
            </article>
          </div>
        </section>

        <section id="live-docket" className="tb-landing-section">
          <div className="tb-landing-container">
            <div className="tb-landing-section__heading">
              <div>
                <div className="tb-landing-kicker">Recent activity</div>
                <h2>Active bounties and challenges</h2>
              </div>
              <a className="tb-landing-inline-link" href="/bounties/react/">
                Browse all bounties <ArrowRight size={16} />
              </a>
            </div>

            <div className="tb-landing-editorial-grid">
              <div className="tb-landing-column">
                <div className="tb-landing-column__header">
                  <ShieldCheck size={18} />
                  <span>Active bounties</span>
                </div>
                {activeItems.length > 0 ? (
                  activeItems.slice(0, 3).map((item, index) => (
                    <LandingCard
                      key={`active-${item.id}`}
                      item={item}
                      label="Active bounty"
                      image={editorialImages[index % editorialImages.length]}
                      tone="light"
                    />
                  ))
                ) : (
                  <div className="tb-landing-empty-card">
                    <h3>No active bounties yet</h3>
                    <p>Be the first to open a claim to public scrutiny.</p>
                  </div>
                )}
              </div>

              <div className="tb-landing-column">
                <div className="tb-landing-column__header tb-landing-column__header--alert">
                  <Siren size={18} />
                  <span>Pending challenges</span>
                </div>
                {challengedItems.length > 0 ? (
                  challengedItems.slice(0, 3).map((item, index) => (
                    <LandingCard
                      key={`challenge-${item.id}`}
                      item={item}
                      label="Under challenge"
                      image={editorialImages[(index + 1) % editorialImages.length]}
                      tone="dark"
                    />
                  ))
                ) : (
                  <div className="tb-landing-empty-card tb-landing-empty-card--featured">
                    <h3>No disputes in motion</h3>
                    <p>The challenge docket is quiet right now.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="tb-landing-section tb-landing-section--soft">
          <div className="tb-landing-container">
            <div className="tb-landing-section__heading">
              <div>
                <div className="tb-landing-kicker">How it works</div>
                <h2>Accountability in six steps</h2>
              </div>
            </div>

            <div className="tb-landing-process-columns">
              {processColumns.map((column, columnIndex) => (
                <div key={`process-column-${columnIndex + 1}`} className="tb-landing-process-list">
                  {column.map(([number, title, description]) => (
                    <article key={number} className="tb-landing-process-item">
                      <div className="tb-landing-process-item__number">{number}</div>
                      <div className="tb-landing-process-item__content">
                        <h3>{title}</h3>
                        <p>{description}</p>
                      </div>
                    </article>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="tb-landing-section">
          <div className="tb-landing-container tb-landing-split-panel">
            <div className="tb-landing-split-panel__copy">
              <div className="tb-landing-kicker">Designed for credibility</div>
              <h2>Less noise, more facts.</h2>
              <p>
                Truth Bounties empowers honest authors to signal their credibility with financial stakes.
                Dishonest authors cannot fake the signal because it will cost them.
                The Truth Bounty badge separates truthful content from the noise.
              </p>
              <a className="tb-landing-primary-button" href="/about-react/">See the model</a>
            </div>
            <div className="tb-landing-split-panel__visual">
              <img src={editorialImages[1]} alt="" />
            </div>
          </div>
        </section>
      </main>

      <footer className="tb-landing-footer">
        <div className="tb-landing-container tb-landing-footer__inner">
          <div>
            <div className="tb-landing-kicker tb-landing-kicker--footer">Truth Bounties</div>
            <h2 className="tb-landing-footer__title">Making information accountable.</h2>
          </div>
          <div className="tb-landing-footer__actions">
            {!demoMode && (
              <a className="tb-landing-primary-button tb-landing-primary-button--footer" href="/accounts/register-react/">
                Create an account
              </a>
            )}
            <a className="tb-landing-link-button tb-landing-link-button--footer" href="/about-react/">Learn more</a>
          </div>
        </div>
      </footer>
    </div>
  )
}

const rootEl = document.getElementById('react-root')
if (rootEl) {
  createRoot(rootEl).render(<GradientLanding />)
}
